import {
	type DefaultMessagesResponse,
	type PlacementMessageFrontendFactory,
	type AnyFrontendStage,
	type PlacementBrowserConfig,
} from '@post-office/message-maker-browser/types';

import { createPlacementFrontend } from '@post-office/message-maker-browser/create-placement-frontend';

import { token } from '@atlaskit/tokens';

import type { FlagProps } from '@atlaskit/flag/types';

import { css } from '@compiled/react';

import type { NextBestAction } from '@post-office/confluence-next-best-action';

import type { EligibleMessage as BackendMessageBody } from '@post-office/shared-contracts/shared-request-middleware-context/context';

import type { SerializableRecord } from '@post-office/serializable-record';

import { usePlacementApi } from '@post-office/use-placement-api';

import {
	type ComponentType,
	type ReactElement,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';

import { useMessagesWithExptAndChoreoDisabled } from '@atlassian/post-office-context/narrow-exposure/use-messages-with-expt-and-choreo-disabled';

import {
	TEMPLATE_XFLOW_EXPERIMENT_KEY,
	type PostOfficeSideNavMessageKey,
	usePostOfficeSideNavMessages,
} from './contexts/PostOfficeSideNavMessageProvider';

import FeatureGates from '@atlaskit/feature-gate-js-client/feature-gates';

import {
	candidatesForNarrowExposure,
	experimentCohortForMessage,
	filterEligibleMessages,
	findExplicitTemplateXflowMessage,
	isNarrowExposureResponseReady,
	selectLegacyDisplayedMessage,
	type DisplayedMessage,
	type SideNavMessage,
} from './select-displayed-message';

const placementConfig: PlacementBrowserConfig = {
	placementId: 'confluence-side-nav',
	options: {
		choreographer: {
			isDefaultEnabled: false,
		},
	},
};

const createFrontend = createPlacementFrontend(placementConfig);

// Props passed from Product to Placement
// These types intentionally mirror Confluence's flags API instead of importing it here.
// That import would reverse the Product -> Placement dependency and risk creating a cycle.
export type PlacementFlagDescriptor = Partial<
	Pick<
		FlagProps,
		| 'id'
		| 'title'
		| 'description'
		| 'actions'
		| 'appearance'
		| 'delayAnnouncement'
		| 'autoDismissSeconds'
	>
> & {
	type?: 'success-circle' | 'success' | 'info' | 'error' | 'warning' | 'custom';
	customIcon?: ReactElement;
	customComponent?: ComponentType<FlagProps>;
	close?: 'auto' | 'never';
	onShown?: () => void;
	onClose?: () => void;
};

type PlacementShowFlagArgs = Pick<
	PlacementFlagDescriptor,
	| 'id'
	| 'title'
	| 'description'
	| 'actions'
	| 'onClose'
	| 'appearance'
	| 'delayAnnouncement'
	| 'autoDismissSeconds'
> & {
	isAutoDismiss?: boolean;
	customIcon?: ReactElement;
};

export type PlacementFlags = {
	showMultipleFlags: (flags: Omit<PlacementFlagDescriptor, 'id'>[]) => Promise<void>;
	showFlag: (flag: PlacementFlagDescriptor) => Promise<unknown>;
	updateFlag: (id: FlagProps['id'] | undefined, patch: PlacementShowFlagArgs) => Promise<void>;
	hideFlag: (id: FlagProps['id'] | undefined) => Promise<void>;
	hideAllFlags: () => Promise<void>;
	setFlagsVisibility: (areFlagsVisible: boolean) => Promise<void>;
	showSuccessFlag: (flag: PlacementShowFlagArgs) => Promise<unknown>;
	showSuccessCircleFlag: (flag: PlacementShowFlagArgs) => Promise<unknown>;
	showInfoFlag: (flag: PlacementShowFlagArgs) => Promise<unknown>;
	showCustomFlag: (flag: PlacementShowFlagArgs) => Promise<unknown>;
	showWarningFlag: (flag: PlacementShowFlagArgs) => Promise<unknown>;
	showErrorFlag: (flag: PlacementShowFlagArgs) => Promise<unknown>;
};

export type PlacementFrontendProps = {
	preloadedInitialData: DefaultMessagesResponse | undefined;
	onDismiss: (messages: { messages: BackendMessageBody<SerializableRecord>[] }) => void;
	onSideNavClick: (
		nextBestActionType: NextBestAction,
		preloadedMessageState: { messages: BackendMessageBody<SerializableRecord>[] },
	) => void;
	onCallback: (callbackKey: NextBestAction) => Promise<void>;
	flags?: PlacementFlags;
	currentRoute?: string;
};

const confluenceSideNavStyles = css({
	display: 'flex',
	margin: token('space.150', '12px'),
});

const SORTED_MESSAGE_KEYS: PostOfficeSideNavMessageKey[] = [
	'MCP_SETUP_NUDGE',
	'CONNECT_3P_TOOLS_NUDGE',
	'CREATE_WITH_ROVO_NUDGE',
	'CREATE_SPACE',
	'CREATE_PAGE',
	'CREATE_WHITEBOARD',
	'1_ON_1_LOOM_NUDGE',
	'LOOM_AI_MEETING_NOTES',
	TEMPLATE_XFLOW_EXPERIMENT_KEY,
	'RESURRECTED_USER_NUDGE',
	'ROVO_EXTENSION_CTA',
];

const getMessageSortKey = (
	message: BackendMessageBody<SerializableRecord>,
): PostOfficeSideNavMessageKey => {
	// @ts-expect-error Property 'experimentKey' does not exist on type 'BackendMessageBody<SerializableRecord>'
	if (message.experimentKey === TEMPLATE_XFLOW_EXPERIMENT_KEY) {
		return TEMPLATE_XFLOW_EXPERIMENT_KEY;
	}

	// @ts-expect-error Property 'nextBestActionType' does not exist on type 'BackendMessageBody<SerializableRecord>'
	return message.nextBestActionType as NextBestAction;
};

// todo: Resolve backend ordering incorrectly
const sortMessages = (messages: BackendMessageBody<SerializableRecord>[] = []) => {
	return [...messages].sort((a, b) => {
		return (
			SORTED_MESSAGE_KEYS.indexOf(getMessageSortKey(a)) -
			SORTED_MESSAGE_KEYS.indexOf(getMessageSortKey(b))
		);
	});
};

const NO_CHOREO_EXPT_MIGRATION_GATE = 'post-office_enable_no-choreo-expt-migration';
const NARROW_EXPOSURE_V2_GATE = 'post-office_enable_narrow-exposure-v2';

export const PlacementFrontend: PlacementMessageFrontendFactory<
	{
		render: AnyFrontendStage;
	},
	PlacementFrontendProps
> = createFrontend(
	(Messages) =>
		({
			preloadedInitialData,
			onDismiss,
			onSideNavClick,
			onCallback,
			flags,
			currentRoute,
		}: PlacementFrontendProps) => {
			const [{ messages, status }, setMessageState] = useState<{
				messages: BackendMessageBody<SerializableRecord>[] | undefined;
				status: 'loading' | 'done';
			}>(() => {
				if (preloadedInitialData) {
					return preloadedInitialData?.messages?.length > 0
						? {
								status: 'done',
								messages: getMessages(preloadedInitialData?.messages),
							}
						: { messages: undefined, status: 'done' };
				}

				return { messages: undefined, status: 'loading' };
			});

			const context = usePostOfficeSideNavMessages();

			const isDone = status === 'done';

			const { data, error, isLoading } = usePlacementApi<DefaultMessagesResponse>('/', {
				autoRefetch: {
					isEnabled: !isDone,
					updateKey: 'confluence-side-nav',
				},
			});

			const handleOnDismissed = useCallback(() => {
				// no more messages until the next fetch
				onDismiss({ messages: [] });

				setMessageState({ messages: undefined, status: 'done' });
			}, [onDismiss]);

			const handleOnSideNavClick = useCallback(
				(nextBestActionType: NextBestAction) => {
					// display the next message
					setMessageState((prevState) => {
						if (prevState.messages) {
							const newMessages = prevState.messages.slice(1);

							onSideNavClick(nextBestActionType, { messages: newMessages });

							return {
								messages: newMessages,
								status: 'done',
							};
						}

						onSideNavClick(nextBestActionType, { messages: [] });

						return prevState;
					});
				},
				[onSideNavClick],
			);

			useEffect(() => {
				if (!isLoading && !isDone) {
					setMessageState({
						messages: getMessages(data?.messages),
						status: 'done',
					});
				}
			}, [isLoading, isDone, data]);

			const filteredMessages = useMemo(
				() => filterEligibleMessages((messages ?? []) as SideNavMessage[], context, currentRoute),
				[messages, context, currentRoute],
			);
			const explicitlyEnabledTemplateXflowMessage = findExplicitTemplateXflowMessage(
				filteredMessages,
				context,
			);
			const isMigrationEnabled =
				FeatureGates.checkGate(NO_CHOREO_EXPT_MIGRATION_GATE) &&
				FeatureGates.checkGate(NARROW_EXPOSURE_V2_GATE);
			const useNarrowExposure =
				isMigrationEnabled &&
				!explicitlyEnabledTemplateXflowMessage &&
				isNarrowExposureResponseReady(filteredMessages);
			const hookCandidates = useNarrowExposure ? candidatesForNarrowExposure(filteredMessages) : [];
			const [hookSelectedMessage] = useMessagesWithExptAndChoreoDisabled(hookCandidates, {
				maxMessagesToDisplay: 1,
				placementId: 'confluence-side-nav',
			});
			const selectionKey = `${filteredMessages
				.map((message) => message.messageInstanceId)
				.join(',')}|${(context?.enabledMessages ?? []).join(',')}|${currentRoute ?? ''}`;
			const displayedMessageSnapshot = useRef<{
				key: string;
				displayed: DisplayedMessage | undefined;
			}>({ key: '', displayed: undefined });

			if (displayedMessageSnapshot.current.key !== selectionKey) {
				displayedMessageSnapshot.current = {
					key: selectionKey,
					displayed: explicitlyEnabledTemplateXflowMessage
						? { message: explicitlyEnabledTemplateXflowMessage }
						: useNarrowExposure
							? hookSelectedMessage
								? {
										message: hookSelectedMessage,
										// TODO PO-10820: Remove this prop and its frontend forwarding when the Rovo CTA experiment is cleaned up.
										experimentCohort: experimentCohortForMessage(hookSelectedMessage),
									}
								: undefined
							: selectLegacyDisplayedMessage(filteredMessages, context),
				};
			}

			const displayedMessage = displayedMessageSnapshot.current.displayed;

			if (error) {
				return null;
			}

			if (status === 'loading') {
				return null;
			}

			if (!displayedMessage) {
				return <div data-empty-side-nav-placement />;
			}

			const { message, experimentCohort } = displayedMessage;

			return (
				<div
					css={
						!FeatureGates.checkGate('navx-global-side-nav-confluence') && confluenceSideNavStyles
					}
				>
					<Messages.render
						{...message}
						// TODO PO-10820: Remove this prop and its frontend forwarding when the Rovo CTA experiment is cleaned up.
						experimentCohort={experimentCohort}
						onSideNavClick={handleOnSideNavClick}
						handleOnDismissed={handleOnDismissed}
						onCallback={onCallback}
						flags={flags}
					/>
				</div>
			);
		},
);

// Trust the result of backend ranking, not a frontend gate or another experiment read.
// Missing metadata (control, fallback or an older response) keeps today's heuristic.
const getMessages = (
	messages: BackendMessageBody<SerializableRecord>[] = [],
): BackendMessageBody<SerializableRecord>[] => {
	if (messages.every((message) => 'rankingMode' in message && message.rankingMode === 'server')) {
		return messages;
	}
	return sortMessages(messages);
};
